<script lang="ts">
  import { Button, Image, Markdown } from '@lib/components';
  import { slide, responsiveStyle, parseResponsiveCSS, navigate } from '@lib/directives';
  import { handleCtaNavigation } from '@lib/utils/navigation';
  import type { HeroBlock } from './types';
  import type { AspectRatio } from '@lib/types/images';
  
  export let block: HeroBlock;
  export let isFullscreen: boolean = false;
  export let onCtaClick: ((action?: string) => void) | undefined = undefined;
  
  let containerEl: HTMLElement;
  let videoElement: HTMLVideoElement;
  let videoState: { ended?: boolean; muted?: boolean } = {};
  
  $: maxWidth = getMaxWidth();
  $: alignment = block.alignment || 'center';
  $: contentClass = `hero-content align-${alignment}`;
  
  // Check if the hero should be clickable (has action/url/href but no button label)
  $: isClickable = block.cta && (block.cta.action || block.cta.url || block.cta.href) && !block.cta.label;
  
  async function handleCtaClick(cta: any) {
    await handleCtaNavigation(cta, onCtaClick);
  }

  function handleVideoEnded(event: Event) {
    const video = event.target as HTMLVideoElement;
    if (block.media?.type === 'video' && !block.media.loop) {
      // Reset to first frame and pause
      video.currentTime = 0;
      video.pause();
      // Update state to show end frame overlay
      videoState = { ...videoState, ended: true };
    }
  }

  function handleVideoClick(event: Event) {
    const video = event.target as HTMLVideoElement;
    if (videoState.ended && block.media?.type === 'video' && !block.media.loop) {
      // Restart video if it ended
      video.currentTime = 0;
      video.play();
      videoState = { ...videoState, ended: false };
    }
  }

  function handleVideoLoaded(event: Event) {
    const video = event.target as HTMLVideoElement;
    videoState = { 
      ended: false,
      muted: video.muted 
    };
  }
  
  // Support config options
  $: fullWidth = block.config?.fullWidth ?? false;
  $: variant = block.config?.variant ?? 'default';
  $: enableAnimations = block.config?.animations?.titleHover || block.config?.animations?.subtitleHover;
  
  // Get overlay configuration
  $: hasOverlay = !!block.overlay;
  $: overlayColor = block.overlay?.color;
  $: overlayDarkColor = block.overlay?.darkColor;

  // Text color priority: config > overlay > default
  $: textColor = block.config?.textColor || block.overlay?.textColor;
  $: textDarkColor = block.config?.textColorDark || block.overlay?.textDarkColor;
  
  // Parse CSS for responsive directive
  $: responsiveCSS = block.css ? parseResponsiveCSS(block.css) : null;

  function getMaxWidth() {
    if (!block.layout?.width) return isFullscreen ? '800px' : '600px';
    if (block.layout.width === 'full') return '100%';
    return `${block.layout.width}px`;
  }

  function getCustomStyles() {
    const styles = [];
    
    // Add background styles first
    const bgStyle = getBackgroundStyle();
    if (bgStyle) styles.push(bgStyle);
    
    // Add custom CSS rules with !important to override any other styles
    if (block.css) {
      // Handle both string and object CSS formats
      if (typeof block.css === 'string') {
        const cssRules = block.css.trim();
        // Process each rule to add !important if not present
        const rules = cssRules.split(';').filter(rule => rule.trim());
        const importantRules = rules.map(rule => {
          const trimmedRule = rule.trim();
          if (trimmedRule && !trimmedRule.includes('!important')) {
            return trimmedRule + ' !important';
          }
          return trimmedRule;
        });
        styles.push(importantRules.join('; ') + ';');
      }
      // For object CSS (ResponsiveCSS), we'll use the directive instead
    }
    
    return styles.join(' ');
  }

  function getThemeColor(color: string) {
    const themeColors: Record<string, string> = {
      'primary': 'var(--theme-primary-color)',
      'secondary': 'var(--theme-secondary-color)',
      'tertiary': 'var(--theme-tertiary-color)',
      'accent': 'var(--theme-accent-color)',
      'success': 'var(--theme-success-color)',
      'warning': 'var(--theme-warning-color)',
      'error': 'var(--theme-error-color)',
      'info': 'var(--theme-info-color)'
    };
    return themeColors[color] || color;
  }

  function getBackgroundStyle() {
    if (!block.config?.background) return '';
    
    const bg = block.config.background;
    
    if (bg.type === 'gradient' && bg.gradient) {
      const fromColor = getThemeColor(bg.gradient.from);
      const toColor = getThemeColor(bg.gradient.to);
      const direction = bg.gradient.direction || '135deg';
      return `background: linear-gradient(${direction}, ${fromColor}, ${toColor});`;
    }
    
    if (bg.type === 'color' && bg.color) {
      const color = getThemeColor(bg.color);
      return `background: ${color};`;
    }
    
    if (bg.type === 'image' && (bg.image?.url || bg.image?.filename)) {
      const imageUrl = bg.image.url || `/images/${bg.image.filename}`;
      return `background-image: url('${imageUrl}'); background-size: cover; background-position: center;`;
    }
    
    return '';
  }
  
  function getObjectPosition(position?: string) {
    const positionMap: Record<string, string> = {
      'left': 'flex-start',
      'center': 'center',
      'right': 'flex-end',
      'top-left': 'flex-start',
      'top-center': 'center',
      'top-right': 'flex-end',
      'bottom-left': 'flex-start',
      'bottom-center': 'center',
      'bottom-right': 'flex-end'
    };
    return positionMap[position || 'center'] || 'center';
  }
  
  function getAlignItems(position?: string) {
    if (position?.startsWith('top')) return 'flex-start';
    if (position?.startsWith('bottom')) return 'flex-end';
    return 'center';
  }
</script>

<div
  class="hero-block"
  class:fullscreen={isFullscreen}
  class:full-width={fullWidth}
  class:gradient-variant={variant === 'gradient'}
  class:has-background={!!block.config?.background}
  class:enable-animations={enableAnimations}
  class:clickable={isClickable}
  style:--max-width={maxWidth}
  style:--height={block.layout?.height}
  style:--overlay-color={overlayColor}
  style:--overlay-dark-color={overlayDarkColor}
  style:--text-color={textColor}
  style:--text-dark-color={textDarkColor}
  style={getCustomStyles()}
  use:responsiveStyle={responsiveCSS || {}}
  use:navigate={isClickable ? { cta: block.cta, onCtaClick } : {}}
  bind:this={containerEl}
>
  {#if block.media}
    <div class="hero-media">
      <div 
        class="hero-item"
        style:--object-fit={block.media.type === 'image' ? (block.media.objectFit || 'cover') : 'cover'}
        style:--object-position={block.media.type === 'image' ? getObjectPosition(block.media.objectPosition) : 'center'}
        style:--align-items={block.media.type === 'image' ? getAlignItems(block.media.objectPosition) : 'center'}
        style:--max-width={block.media.type === 'image' ? block.media.maxWidth : undefined}
      >
        {#if block.media.type === 'gradient'}
          <!-- Gradient background -->
          <div 
            class="gradient-item"
            style="background: linear-gradient({block.media.direction || '135deg'}, {getThemeColor(block.media.from)}, {getThemeColor(block.media.to)});"
          ></div>
        {:else if block.media.type === 'image'}
          {#if block.media.objectFit === 'contain' || block.media.objectFit === 'none'}
            <Image
              src={block.media.url || block.media.filename}
              alt={block.media.alt || ''}
              aspectRatio={block.media.aspectRatio as AspectRatio}
              backgroundSize={block.media.objectFit === 'contain' ? 'contain' : 'cover'}
              sources={block.media.sources}
              originalSrc={block.media.originalSrc}
            />
          {:else}
            <Image
              src={block.media.url || block.media.filename}
              alt={block.media.alt || ''}
              aspectRatio={block.media.aspectRatio as AspectRatio}
              backgroundSize="cover"
              sources={block.media.sources}
              originalSrc={block.media.originalSrc}
            />
          {/if}
        {:else if block.media.type === 'video'}
          <div class="video-container" class:has-controls={block.media.controls}>
            <video
              bind:this={videoElement}
              autoplay={block.media.autoPlay !== false}
              muted={block.media.mute !== false}
              loop={block.media.loop !== false}
              controls={block.media.controls}
              playsinline
              poster="/videos/template/project/{block.media.filename}.jpg"
              preload="metadata"
              on:ended={handleVideoEnded}
              on:loadeddata={handleVideoLoaded}
              on:click={handleVideoClick}
            >
              <!-- Multiple sources for different video sizes -->
              <source src="/videos/template/project/{block.media.filename}.mp4" type="video/mp4" />
              <source src="/videos/template/project/{block.media.filename}_w800.mp4" type="video/mp4" />
              <source src="/videos/template/project/{block.media.filename}_w400.mp4" type="video/mp4" />
              <track kind="captions" />
            </video>
            
            <!-- End frame overlay -->
            {#if videoState.ended && !block.media.loop}
              <div 
                class="video-end-frame"
                on:click={(e) => {
                  e.stopPropagation();
                  handleVideoClick(e);
                }}
                role="button"
                tabindex="0"
                aria-label="Replay video"
                on:keydown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleVideoClick(e);
                  }
                }}
              >
                <img 
                  src="/videos/template/project/{block.media.filename}.jpg" 
                  alt={block.media.alt || 'Video thumbnail'}
                  style="width: 100%; height: 100%; object-fit: cover;"
                />
                <!-- Play button overlay -->
                <div class="play-button-overlay">
                  <svg class="play-icon" viewBox="0 0 24 24" fill="none">
                    <path d="M8 5v14l11-7L8 5z" fill="currentColor" />
                  </svg>
                </div>
              </div>
            {/if}
            
            {#if block.media.mute !== false && !block.media.controls}
              <button 
                class="unmute-btn"
                on:click={(e) => { 
                  e.stopPropagation(); 
                  if (videoElement) { 
                    videoElement.muted = !videoElement.muted; 
                    videoState = { ...videoState, muted: videoElement.muted };
                  }
                }}
                aria-label={videoState.muted !== false ? "Unmute video" : "Mute video"}
              >
                {videoState.muted !== false ? '🔊' : '🔇'}
              </button>
            {/if}
          </div>
        {/if}
        
        {#if block.overlay}
          <div 
            class="hero-item-overlay"
            style:background-color={block.overlay.color}
            style:--overlay-dark-color={block.overlay.darkColor}
          ></div>
        {/if}
      </div>
    </div>
  {/if}
  
  {#if hasOverlay || variant === 'gradient'}
    <div 
      class={contentClass}
      class:has-overlay={hasOverlay}
      class:gradient-content={variant === 'gradient'}
    >
      {#if block.content && block.markdown}
        <div
          class="hero-text"
          use:slide={{ translate: 3, duration: 400, delay: 150, startOpacity: 0 }}
        >
          <Markdown content={block.content} align={alignment} inline />
        </div>
      {:else if block.title || block.subtitle}
        <div
          class="hero-text"
          use:slide={{ translate: 3, duration: 400, delay: 150, startOpacity: 0 }}
        >
          {#if block.title}
            <h1
              class="hero-title"
              class:hover-scale={block.config?.animations?.titleHover}
            >{block.title}</h1>
          {/if}
          {#if block.subtitle}
            <p
              class="hero-subtitle"
              class:hover-scale={block.config?.animations?.subtitleHover}
            >{block.subtitle}</p>
          {/if}
        </div>
      {/if}
      
      {#if block.cta && block.cta.label}
        <div 
          class="hero-cta"
          use:slide={{ translate: 5, duration: 400, delay: 300, startOpacity: 0 }}
        >
          <Button 
            label={block.cta.label}
            variant={block.cta.variant || 'primary'}
            size="lg"
            inline={true}
            on:click={() => handleCtaClick(block.cta)}
          />
        </div>
      {/if}
    </div>
  {:else if block.title || block.subtitle || block.content || (block.cta && block.cta.label)}
    <div class={contentClass}>
      {#if block.content && block.markdown}
        <div
          class="hero-text"
          use:slide={{ translate: 3, duration: 400, delay: 150, startOpacity: 0 }}
        >
          <Markdown content={block.content} align={alignment} inline />
        </div>
      {:else if block.title || block.subtitle}
        <div
          class="hero-text"
          use:slide={{ translate: 3, duration: 400, delay: 150, startOpacity: 0 }}
        >
          {#if block.title}
            <h1 class="hero-title">{block.title}</h1>
          {/if}
          {#if block.subtitle}
            <p class="hero-subtitle">{block.subtitle}</p>
          {/if}
        </div>
      {/if}
      
      {#if block.cta && block.cta.label}
        <div 
          class="hero-cta"
          use:slide={{ translate: 5, duration: 400, delay: 300, startOpacity: 0 }}
        >
          <Button 
            label={block.cta.label}
            variant={block.cta.variant || 'primary'}
            size="lg"
            inline={true}
            on:click={() => handleCtaClick(block.cta)}
          />
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="postcss">
  .hero-block {
    position: relative;
    width: 100%;
    max-width: var(--max-width);
    height: var(--height, auto);
    min-height: var(--height, fit-content);
    padding: 1rem 0;
    margin: 0 auto;
    overflow: hidden;
    border-radius: var(--theme-radius, 0.375rem);
    container-type: inline-size;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  
  /* Remove vertical padding when height is explicitly set (e.g., inside carousel) */
  .hero-block[style*="--height"] {
    padding: 0;
  }
  
  /* Remove border radius when full-width */
  .hero-block.full-width {
    border-radius: 0;
  }
  
  /* Remove border radius on mobile when full-width */
  @container (max-width: 600px) {
    .hero-block.full-width {
      border-radius: 0;
    }
  }
  
  .hero-media {
    position: absolute;
    inset: 0;
    display: flex;
  }
  
  .hero-item {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: var(--align-items, center);
    justify-content: var(--object-position, center);
  }
  
  .hero-item :global(.image-wrapper) {
    width: var(--max-width, 100%);
    height: 100%;
  }
  
  .video-container {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  .hero-item video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Improve video controls behavior */
  .video-container.has-controls video::-webkit-media-controls-panel {
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .video-container.has-controls:hover video::-webkit-media-controls-panel {
    opacity: 1;
  }

  /* Keep controls visible on mobile when controls=true */
  @media (max-width: 768px) {
    .video-container.has-controls video::-webkit-media-controls-panel {
      opacity: 1;
    }
  }

  /* Firefox controls */
  .video-container.has-controls video::-moz-media-controls {
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .video-container.has-controls:hover video::-moz-media-controls {
    opacity: 1;
  }

  @media (max-width: 768px) {
    .video-container.has-controls video::-moz-media-controls {
      opacity: 1;
    }
  }

  .video-end-frame {
    position: absolute;
    inset: 0;
    z-index: 5;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.1);
    transition: background-color 0.3s ease;
  }

  .video-end-frame:hover {
    background: rgba(0, 0, 0, 0.2);
  }

  .play-button-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
    width: 4rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    pointer-events: none;
  }

  .video-end-frame:hover .play-button-overlay {
    background: rgba(0, 0, 0, 0.6);
    transform: translate(-50%, -50%) scale(1.05);
  }

  .play-icon {
    width: 1.5rem;
    height: 1.5rem;
    color: white;
    opacity: 0.9;
    margin-left: 0.1rem;
  }
  
  .unmute-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    border: none;
    border-radius: 50%;
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 1.2rem;
    transition: all 0.2s ease;
    z-index: 10;
  }
  
  .unmute-btn:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
  
  .unmute-btn:active {
    transform: scale(0.95);
  }
  
  :global([data-theme='dark']) .hero-item {
    background-color: var(--item-dark-bg);
  }
  
  .hero-content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 2rem;
    box-sizing: border-box;
    pointer-events: none;
    max-width: 1080px;
    margin: 0 auto;
  }
  
  /* Re-enable pointer events for interactive content */
  .hero-content .hero-text,
  .hero-content .hero-cta,
  .hero-content button,
  .hero-content a {
    pointer-events: auto;
  }
  
  .hero-item-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
  }

  .hero-content.has-overlay {
    color: var(--text-color);
  }
  
  /* Alignment variations */
  .hero-content.align-center {
    align-items: center;
    text-align: center;
  }
  
  .hero-content.align-left {
    align-items: flex-start;
    text-align: left;
  }
  
  .hero-content.align-right {
    align-items: flex-end;
    text-align: right;
  }
  
  /* Ensure CTA follows alignment */
  .hero-content.align-left .hero-cta,
  .hero-content.align-right .hero-cta {
    text-align: right;
  }
  
  .hero-text {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  /* Improved typography hierarchy */
  .hero-title {
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.2;
    margin: 0;
  }
  
  .hero-subtitle {
    font-size: 1.4rem;
    line-height: 1.5;
    margin: 0;
    opacity: 0.95;
  }

  .hero-cta {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
  
  /* Mobile adjustments */
  @container (max-width: 600px) {
    .hero-content {
      padding: 1rem 2rem;
    }
    
    .hero-title {
      font-size: 1.75rem;
    }
    
    .hero-subtitle {
      font-size: 1.25rem;
      line-height: 1.2;
    }
  }
  
  /* Fullscreen adjustments */
  .hero-block.fullscreen {
    max-width: var(--max-width);
  }

  /* Gradient variant styling */
  .hero-block.gradient-variant {
    background-color: transparent;
  }

  .hero-block.gradient-variant .hero-media {
    display: none;
  }

  .hero-content.gradient-content {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    bottom: auto;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    color: var(--text-color, white);
    padding: 2rem;
  }

  /* When there's no media background, position content relatively */
  .hero-block:not(:has(.hero-media)) .hero-content {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    bottom: auto;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /* Hover animations */
  .hover-scale {
    transition: transform 0.4s ease-out;
  }

  .hero-block.enable-animations:hover .hero-title.hover-scale {
    transform: scale(1.05);
    transition-duration: 0.6s;
  }

  .hero-block.enable-animations:hover .hero-subtitle.hover-scale {
    transform: scale(1.03);
    transition-duration: 0.8s;
  }

  /* Clickable banner styling */
  .hero-block.clickable {
    cursor: pointer;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .hero-block.clickable:hover {
    transform: scale(1.02);
    opacity: 0.95;
  }

  .hero-block.clickable:active {
    transform: scale(0.98);
  }

  .hero-block.clickable:focus {
    outline: 2px solid var(--theme-primary-color, #007bff);
    outline-offset: 2px;
  }

  /* Gradient item styling */
  .gradient-item {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
  
  /* Dark mode support for overlay */
  :global([data-theme='dark']) .hero-item-overlay {
    background-color: var(--overlay-dark-color) !important;
  }

  :global([data-theme='dark']) .hero-content.has-overlay {
    color: var(--text-dark-color);
  }
</style>